import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/lib/services/community";
import { useAuth } from "@/context/AuthContext";

const KEYS = {
  unreadCount: ["notifications", "unread-count"] as const,
  list: ["notifications", "list"] as const,
};

/**
 * Polls unread notification count every 30s.
 * Only runs when the user is logged in.
 */
export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: KEYS.unreadCount,
    queryFn: getUnreadCount,
    enabled: !!user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  });
}

/**
 * Refreshes the forum feed whenever a new notification arrives.
 *
 * The unread-count poll (every 30s) is our heartbeat: an *increase* means a new
 * notification landed — which includes upvotes/comments on the user's posts. We
 * invalidate the feed so its vote/comment counts refetch to authoritative
 * values instead of showing the snapshot from page-load time. Decreases
 * (mark-as-read) are ignored. Latency is bounded by the poll interval.
 *
 * Subscribes to the existing unread-count query by key, so it triggers no extra
 * network request. Mount once in a long-lived component (the header bell).
 */
export function useRefreshFeedOnNewNotification() {
  const qc = useQueryClient();
  const { data: unreadCount } = useUnreadCount();
  const prev = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (unreadCount === undefined) return;
    if (prev.current !== undefined && unreadCount > prev.current) {
      qc.invalidateQueries({ queryKey: ["forum", "feed"] });
    }
    prev.current = unreadCount;
  }, [unreadCount, qc]);
}

/**
 * Fetches the full notification list.
 * Meant to be enabled when the notification panel is open.
 */
export function useNotificationList(enabled: boolean) {
  const { user } = useAuth();

  return useQuery({
    queryKey: KEYS.list,
    queryFn: getNotifications,
    enabled: !!user && enabled,
    staleTime: 10_000,
  });
}

/**
 * Marks a single notification as read.
 * Optimistically updates both list and unread count.
 */
export function useMarkRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.unreadCount });
    },
  });
}

/**
 * Marks all notifications as read.
 */
export function useMarkAllRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.unreadCount });
    },
  });
}
